import { Component, signal, Input, inject, PLATFORM_ID, ViewChild, ElementRef, AfterViewChecked, OnInit, OnDestroy } from '@angular/core';
import { isPlatformBrowser, DOCUMENT } from '@angular/common';
import { RouterLink } from '@angular/router';

export interface PromotionBarData {
  id: string;
  text: string;
  ctaText?: string;
  ctaLink?: string;
  externalLink?: boolean;
  style?: 'brand' | 'gold' | 'urgent' | 'dark';
  dismissible?: boolean;
  /** Raw HTML for custom ad banners — rendered as-is */
  htmlContent?: string;
  /** Image URL for ad banners */
  mediaUrl?: string;
}

@Component({
  selector: 'app-promotion-bar',
  standalone: true,
  imports: [RouterLink],
  template: `
    @if (!dismissed()) {
      <div #barEl class="fixed top-0 left-0 right-0 z-[60] overflow-hidden text-white"
           [class.bg-gradient-to-r]="true"
           [class.from-brand-700]="!promo.style || promo.style === 'brand'"
           [class.via-brand-600]="!promo.style || promo.style === 'brand'"
           [class.to-brand-700]="!promo.style || promo.style === 'brand'"
           [class.from-gold-600]="promo.style === 'gold'"
           [class.via-gold-500]="promo.style === 'gold'"
           [class.to-gold-600]="promo.style === 'gold'"
           [class.from-red-600]="promo.style === 'urgent'"
           [class.via-red-500]="promo.style === 'urgent'"
           [class.to-red-600]="promo.style === 'urgent'"
           [class.!bg-surface-950]="promo.style === 'dark'"
           [class.!text-surface-300]="promo.style === 'dark'"
           [class.!bg-none]="promo.style === 'dark'"
           role="complementary"
           [attr.aria-label]="promo.htmlContent ? 'Advertisement' : 'Promotion: ' + promo.text">

        <!-- Animated shimmer -->
        @if (!promo.htmlContent && !promo.mediaUrl) {
          <div class="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden="true">
            <div class="absolute -inset-full animate-shimmer"
                 style="background: linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.06) 50%, transparent 100%);">
            </div>
          </div>
        }

        <!-- Custom HTML ad -->
        @if (promo.htmlContent) {
          <div class="mx-auto max-w-7xl px-4 py-2 text-center" [innerHTML]="promo.htmlContent"></div>
        }
        <!-- Image ad banner -->
        @else if (promo.mediaUrl) {
          <div class="mx-auto max-w-7xl px-4 py-1.5 text-center">
            @if (promo.ctaLink) {
              <a [href]="promo.externalLink ? promo.ctaLink : null"
                 [routerLink]="!promo.externalLink ? promo.ctaLink : null"
                 [target]="promo.externalLink ? '_blank' : undefined"
                 [rel]="promo.externalLink ? 'noopener noreferrer sponsored' : null">
                <img [src]="promo.mediaUrl" [alt]="promo.text" class="h-10 mx-auto object-contain" />
              </a>
            } @else {
              <img [src]="promo.mediaUrl" [alt]="promo.text" class="h-10 mx-auto object-contain" />
            }
          </div>
        }
        <!-- Standard promotion -->
        @else {
          <div class="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-2.5 relative z-10">
            <div class="flex items-center justify-center gap-3 text-sm">
              @if (promo.style === 'urgent') {
                <span class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[0.65rem] font-bold uppercase tracking-wider bg-white/20 backdrop-blur-sm">
                  Limited Time
                </span>
              }
              <span class="font-medium">{{ promo.text }}</span>
              @if (promo.ctaText && promo.ctaLink) {
                @if (promo.externalLink) {
                  <a [href]="promo.ctaLink"
                     target="_blank" rel="noopener noreferrer"
                     class="inline-flex items-center gap-1.5 px-3.5 py-1 rounded-full text-xs font-bold transition-all duration-200 text-white"
                     [class.bg-white/20]="!promo.style || promo.style === 'brand' || promo.style === 'gold'"
                     [class.hover:bg-white/30]="!promo.style || promo.style === 'brand' || promo.style === 'gold'"
                     [class.bg-white]="promo.style === 'urgent'"
                     [class.!text-red-600]="promo.style === 'urgent'"
                     [class.hover:bg-white/90]="promo.style === 'urgent'"
                     [class.bg-white/10]="promo.style === 'dark'"
                     [class.hover:bg-white/20]="promo.style === 'dark'">
                    {{ promo.ctaText }}
                    <svg aria-hidden="true" class="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5"><path stroke-linecap="round" stroke-linejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" /></svg>
                  </a>
                } @else {
                  <a [routerLink]="promo.ctaLink"
                     class="inline-flex items-center gap-1.5 px-3.5 py-1 rounded-full text-xs font-bold transition-all duration-200 text-white"
                     [class.bg-white/20]="!promo.style || promo.style === 'brand' || promo.style === 'gold'"
                     [class.hover:bg-white/30]="!promo.style || promo.style === 'brand' || promo.style === 'gold'"
                     [class.bg-white]="promo.style === 'urgent'"
                     [class.!text-red-600]="promo.style === 'urgent'"
                     [class.hover:bg-white/90]="promo.style === 'urgent'"
                     [class.bg-white/10]="promo.style === 'dark'"
                     [class.hover:bg-white/20]="promo.style === 'dark'">
                    {{ promo.ctaText }}
                    <svg aria-hidden="true" class="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5"><path stroke-linecap="round" stroke-linejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" /></svg>
                  </a>
                }
              }
            </div>
          </div>
        }

        <!-- Dismiss button -->
        @if (promo.dismissible !== false) {
          <button (click)="dismiss()" aria-label="Dismiss promotion"
                  class="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 rounded-full transition-colors hover:bg-white/15 z-20">
            <svg aria-hidden="true" class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
              <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        }
      </div>

      <!-- Spacer to push content below the fixed bar -->
      <div #spacerEl class="w-full"></div>
    }
  `,
  styles: [`
    :host { display: block; }
    @keyframes shimmer {
      0% { transform: translateX(-100%); }
      100% { transform: translateX(100%); }
    }
    .animate-shimmer { animation: shimmer 3s ease-in-out infinite; }
  `],
})
export class PromotionBarComponent implements OnInit, AfterViewChecked, OnDestroy {
  private platformId = inject(PLATFORM_ID);
  private doc = inject(DOCUMENT);

  @Input({ required: true }) promo!: PromotionBarData;

  @ViewChild('barEl') barRef?: ElementRef<HTMLElement>;
  @ViewChild('spacerEl') spacerRef?: ElementRef<HTMLElement>;

  dismissed = signal(false);

  private lastHeight = 0;

  ngOnInit() {
    if (isPlatformBrowser(this.platformId)) {
      if (sessionStorage.getItem(`promo-dismissed-${this.promo.id}`)) {
        this.dismissed.set(true);
        this.setBarHeight(0);
      }
    }
  }

  ngAfterViewChecked() {
    if (!isPlatformBrowser(this.platformId) || this.dismissed()) return;
    const barEl = this.barRef?.nativeElement;
    if (barEl) {
      const h = barEl.offsetHeight;
      if (h !== this.lastHeight) {
        this.lastHeight = h;
        this.setBarHeight(h);
        if (this.spacerRef) {
          this.spacerRef.nativeElement.style.height = h + 'px';
        }
      }
    }
  }

  ngOnDestroy() {
    this.setBarHeight(0);
  }

  dismiss() {
    this.dismissed.set(true);
    this.setBarHeight(0);
    if (isPlatformBrowser(this.platformId)) {
      sessionStorage.setItem(`promo-dismissed-${this.promo.id}`, '1');
    }
  }

  private setBarHeight(h: number) {
    if (isPlatformBrowser(this.platformId)) {
      this.doc.documentElement.style.setProperty('--promo-bar-height', h + 'px');
    }
  }

}
